import { useMemo, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import TextInput from "components/Form/Inputs/common/TextInput";
import RHFSelectInput from "components/Form/Inputs/common/RHFSelectInput";
import SwitchInput from "components/Form/Inputs/common/SwitchInput";
import RadioButtonGroup from "components/Form/Inputs/common/RadioButtonGroup";
import { useRuleDataContext } from "../../context/RuleDataProvider";
import RuleMessageOptionContent from "./RuleMessageOptionContent";
import RuleMessageDynamicEmail from "./RuleMessageDynamicEmail";
import RuleMessageOption from "./RuleMessageOption";
import { MANUAL_RULE_TYPE_LABEL } from "constants/index";
import { nameCapitalize } from "utils/coreUtils";
import { RuleMessageObjectType } from "../../validation/ruleFormSchema";
import { RuleMessageType } from "../../types";

export default function SettingsTab({ viewOnly, ruleOverviewDropdown }) {
  const {
    register,
    formState: { errors: formError },
    setValue,
    getValues,
    watch,
  } = useFormContext();
  const { type, ruleTypes, routingUsers, getNextPriorityForRule } =
    useRuleDataContext();
  const params = useParams();
  const [t] = useTranslation();
  const [showRoutingUser, setShowRoutingUser] = useState(false);
  const [groupOptions, setGroupOptions] = useState(ruleOverviewDropdown);
  const ruleTypeIdWatch = watch("rulesTypeId");
  const messageOptions: RuleMessageObjectType = watch("messages");

  const datastreamList = useSelector(
    (state) => (state as any)?.datastream?.list
  );
  const companyDetails = useSelector((state) => (state as any)?.company);
  const isDynamicMessageConstruction =
    companyDetails?.subscriptions?.dynamicMessageConstruction;
  const internalMessageState = messageOptions?.[RuleMessageType.INTERNAL];
  const externalMessageState = messageOptions?.[RuleMessageType.EXTERNAL];
  const emailMessageState = messageOptions?.[RuleMessageType.EMAIL];

  const routingUserOptions = useMemo(
    () => [
      {
        label: nameCapitalize("Select None"),
        value: "",
      },
      ...routingUsers.map((e) => ({
        ...e,
        label: nameCapitalize(e.firstName) + " " + nameCapitalize(e.lastName),
        value: e.companyRouteUserId,
      })),
    ],
    [routingUsers]
  );
  const currentPriority = useMemo(() => {
    return getValues("priority");
  }, []);

  const selectedMessageOption = useMemo(() => {
    return Object.keys(messageOptions).find(
      (option) => messageOptions[option].isMessageActive
    );
  }, [messageOptions]);

  const onMessageAttributeChange = (key, value) => {
    const currentMessage = messageOptions[selectedMessageOption];

    const messageObject = {
      ...messageOptions,
      [selectedMessageOption]: {
        ...currentMessage,
        [key]: value,
      },
    };
    setValue("messages", messageObject);
  };

  useEffect(() => {
    const manualRuleType = ruleTypes.find(
      (type) => type?.label?.toLowerCase() === MANUAL_RULE_TYPE_LABEL
    );
    const isManualType =
      Number(ruleTypeIdWatch) === manualRuleType?.rulesTypeId;
    setShowRoutingUser(isManualType);
    if (!isManualType) {
      setValue("routingUserId", null);
    }
  }, [ruleTypeIdWatch]);

  return (
    <div className="operator-area">
      <div className="setting-control mb-4">
        <h6 className="text-primary mb-3">Settings</h6>
        <div className="setting-control-operator">
          <div className="row">
            <TextInput
              label={t("createRuleEngine.settingsTab.ruleName.label")}
              isRequired={true}
              placeholder={t(
                "createRuleEngine.settingsTab.ruleName.placeholder"
              )}
              groupProps={{
                controlId: "ruleName",
                className: "col-lg-6 col-md-6",
              }}
              labelProps={{
                className: "mt-2",
              }}
              disabled={viewOnly}
              {...register("ruleName")}
              error={formError.ruleName?.message}
            />

            <RHFSelectInput
              options={groupOptions}
              label={t("createRuleEngine.settingsTab.groupName.label")}
              groupProps={{
                controlId: "groupName",
                className: "col-lg-6 col-md-6",
              }}
              isRequired={true}
              disabled={viewOnly}
              labelProps={{
                className: "mt-2",
              }}
              onCreateOption={(newOption) =>
                setGroupOptions(() => [...ruleOverviewDropdown, newOption])
              }
            />

            <TextInput
              type="number"
              label={t("createRuleEngine.settingsTab.priority.label")}
              placeholder={t(
                "createRuleEngine.settingsTab.priority.placeholder"
              )}
              groupProps={{
                controlId: "priority",
                className: "col-lg-6 col-md-6",
              }}
              labelProps={{
                className: "mt-3",
              }}
              disabled={true}
              {...register("priority")}
              error={formError.priority?.message}
            />

            <SwitchInput
              label={t("createRuleEngine.settingsTab.status.label")}
              groupProps={{
                controlId: "isActive",
                className: "col-lg-6 col-md-6",
              }}
              labelProps={{
                className: "mt-3",
              }}
              disabled={viewOnly}
              containerClass="switch-input-container"
              {...register("isActive")}
              onChange={async (e) => {
                setValue("isActive", e.target.checked);
                if (!e.target.checked) {
                  setValue("priority", -1);
                } else {
                  if (currentPriority !== -1) {
                    setValue("priority", currentPriority);
                  } else {
                    const priority = await getNextPriorityForRule();
                    setValue("priority", priority);
                  }
                }
              }}
              error={formError.isActive?.message}
            />

            <TextInput
              label={t("createRuleEngine.settingsTab.rulesDescription.label")}
              isRequired={false}
              placeholder={t(
                "createRuleEngine.settingsTab.rulesDescription.placeholder"
              )}
              groupProps={{
                controlId: "rulesDescription",
                className: "col-lg-8 col-md-8",
              }}
              labelProps={{
                className: "mt-3",
              }}
              as="textarea"
              rows={5}
              disabled={viewOnly}
              style={{
                minHeight: "120px",
              }}
              {...register("rulesDescription")}
              error={formError.rulesDescription?.message}
            />

            <RadioButtonGroup
              label={t("createRuleEngine.settingsTab.routingUser.ruleType")}
              isRequired={true}
              options={ruleTypes.map((type) => ({
                label: type.label,
                value: type.rulesTypeId,
              }))}
              disabled={viewOnly}
              groupProps={{
                controlId: "rulesTypeId",
                className: "col-12",
              }}
              labelProps={{
                className: "mt-3",
              }}
              inline
              {...register("rulesTypeId")}
              value={Number(getValues("rulesTypeId"))}
              error={formError.rulesTypeId?.message}
            />

            {showRoutingUser && (
              <RHFSelectInput
                options={routingUserOptions}
                label={t("createRuleEngine.settingsTab.routingUser.label")}
                placeholder={t(
                  "createRuleEngine.settingsTab.routingUser.placeholder"
                )}
                noOptionsMessage={() =>
                  t("createRuleEngine.settingsTab.routingUser.noOptionsMessage")
                }
                groupProps={{
                  controlId: "routingUserId",
                  className: "col-12",
                }}
                isRequired={true}
                labelProps={{
                  className: "mt-2",
                }}
                disabled={viewOnly}
              />
            )}
          </div>
        </div>
      </div>
      <div className="setting-control mt-4">
        <h6 className="text-primary mb-3">
          {t("createRuleEngine.settingsTab.messageOptions.title")}
          <span style={{ color: "red" }}> *</span>
        </h6>

        <div className="setting-control-operator">
          <div className="row">
            <nav>
              <div
                className="nav nav-tabs border-0"
                id="nav-tab"
                role="tablist"
              >
                {Object.values(messageOptions).map((option) => {
                  const value = option.messageType?.toLowerCase();
                  const id = `${value}-tab`;
                  return (
                    <RuleMessageOption
                      key={id}
                      id={id}
                      target={value}
                      label={value}
                      isChecked={option.isMessageActive}
                      onChange={() => {
                        const messageObject = Object.keys(
                          messageOptions
                        )?.reduce((acc, messageType) => {
                          return {
                            ...acc,
                            [messageType]: {
                              ...messageOptions[messageType],
                              isMessageActive:
                                messageType === option.messageType,
                            },
                          };
                        }, {});
                        setValue("messages", messageObject);
                      }}
                    />
                  );
                })}
              </div>
            </nav>
            <div className="tab-content" id="nav-tabContent">
              <RuleMessageOptionContent
                id="internal"
                isDynamicMessageConstruction={isDynamicMessageConstruction}
                viewOnly={viewOnly}
                configList={datastreamList}
                placeholder={t(
                  "createRuleEngine.settingsTab.messageOptions.internalMessage.messagePlaceholder"
                )}
                isDynamicMessageType={
                  internalMessageState?.isDynamicMessageType
                }
                onMessageAttributeChange={onMessageAttributeChange}
                staticMessage={internalMessageState?.message}
                dynamicMessage={internalMessageState?.dynamicMessage}
                isActive={selectedMessageOption === RuleMessageType.INTERNAL}
              />

              <RuleMessageOptionContent
                id="external"
                isDynamicMessageConstruction={isDynamicMessageConstruction}
                viewOnly={viewOnly}
                configList={datastreamList}
                placeholder={t(
                  "createRuleEngine.settingsTab.messageOptions.externalMessage.message1Placeholder"
                )}
                isDynamicMessageType={
                  externalMessageState?.isDynamicMessageType
                }
                onMessageAttributeChange={onMessageAttributeChange}
                staticMessage={externalMessageState?.message}
                dynamicMessage={externalMessageState?.dynamicMessage}
                isActive={selectedMessageOption === RuleMessageType.EXTERNAL}
              />

              <RuleMessageOptionContent
                id="email"
                isDynamicMessageConstruction={isDynamicMessageConstruction}
                viewOnly={viewOnly}
                configList={datastreamList}
                placeholder={t(
                  "createRuleEngine.settingsTab.messageOptions.emailMessage.messagePlaceholder"
                )}
                isDynamicMessageType={emailMessageState?.isDynamicMessageType}
                onMessageAttributeChange={onMessageAttributeChange}
                staticMessage={emailMessageState?.message}
                dynamicMessage={emailMessageState?.dynamicMessage}
                isActive={selectedMessageOption === RuleMessageType.EMAIL}
              >
                <RuleMessageDynamicEmail
                  isDynamicEmailType={emailMessageState?.isDynamicEmailType}
                  onMessageAttributeChange={onMessageAttributeChange}
                  messageEmail={emailMessageState?.messageEmail}
                  viewOnly={viewOnly}
                  configList={datastreamList}
                  dynamicEmailMessage={emailMessageState?.dynamicEmailMessage}
                  placeholder={t(
                    "createRuleEngine.settingsTab.messageOptions.emailMessage.messagePlaceholder"
                  )}
                />
              </RuleMessageOptionContent>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
